import type { TrainerAvatar as TrainerAvatarData } from '../../lib/types';

/**
 * A trainer's avatar. The one component that draws one.
 *
 * There is deliberately no second implementation anywhere — a guild roster that
 * writes its own `<img>` is a second set of decisions about size, rendering and
 * the empty state, and the two drift the first time either changes. That is the
 * same argument `TrainerCard` makes for being shared between the profile and
 * the customization preview.
 *
 * Three rules, and all three come from the artwork being pixel art somebody
 * else drew:
 *
 * **Never resampled.** `image-rendering: pixelated` plus integer scaling only.
 * A trainer sprite blurred by bilinear filtering is not the sprite; and the
 * source's terms say the artwork must not be edited, which a smooth 1.37x
 * upscale plainly is.
 *
 * **Never cropped to a circle.** The brief is explicit about this and it is not
 * only taste: these are full-body sprites with heads near the top, so a circular
 * mask decapitates most of the catalog. The frame is a square well, which is
 * what the rest of this game's chrome already is.
 *
 * **Never recoloured.** Role styling — the Founder gold, the Developer blue —
 * is applied to the *frame around* the avatar by `RoleBadge` and the trainer
 * card's own classes. Nothing here tints the pixels.
 */

export interface TrainerAvatarProps {
  avatar: TrainerAvatarData | null | undefined;
  /**
   * Rendered size in CSS pixels.
   *
   * Sprites are 80x80 at source, so 40 / 80 / 160 are the exact ratios. Other
   * values still render correctly — `pixelated` keeps the edges hard — but the
   * presets are what the rest of the UI uses.
   */
  size?: number;
  /** A ring key from `Profile.frame`. Drawn around, never over, the sprite. */
  frame?: string | null;
  /** Turns the sprite into a decorative image when the name is already shown. */
  decorative?: boolean;
  className?: string;
  /** `eager` for the one avatar at the top of a profile; lazy everywhere else. */
  loading?: 'eager' | 'lazy';
}

export function TrainerAvatar({
  avatar,
  size = 64,
  frame,
  decorative = true,
  className = '',
  loading = 'lazy',
}: TrainerAvatarProps) {
  const url = avatar?.url ?? null;

  const portrait = (
    <span
      className={`tavatar ${url ? '' : 'tavatar--empty'} ${className}`}
      style={{ '--tavatar-size': `${size}px` } as React.CSSProperties}
      // The label carries the artist when there is one, so a credit is present
      // wherever the sprite is — a tooltip is not the credits page, but an
      // avatar that has quietly lost its attribution is the thing the brief
      // forbids, and the page exists as well.
      title={
        avatar && avatar.kind === 'TRAINER'
          ? avatar.artistCredit
            ? `${avatar.label} — sprite by ${avatar.artistCredit}`
            : avatar.label
          : undefined
      }
    >
      {url ? (
        <img
          className="pixel tavatar__img"
          src={url}
          alt={decorative ? '' : (avatar?.label ?? 'Trainer avatar')}
          aria-hidden={decorative || undefined}
          width={size}
          height={size}
          loading={loading}
          // Hundreds of sprites in a grid is hundreds of layout-affecting
          // loads. Telling the browser they are independent lets it paint the
          // page without waiting on the ones below the fold.
          decoding="async"
          draggable={false}
        />
      ) : (
        // Not an error, and not styled like one. A trainer with no avatar is
        // somebody who has not picked yet.
        <span className="tavatar__none" aria-hidden>
          ◓
        </span>
      )}
    </span>
  );

  if (!frame || frame === 'none') return portrait;

  // The badge-granted ring. Reads its key from a data attribute so an unknown
  // one renders no frame rather than breaking — a client can be older than the
  // badge that granted it.
  return (
    <span className="frame" data-frame={frame}>
      {portrait}
    </span>
  );
}

/**
 * The credit line, on its own.
 *
 * A component rather than a string so that every place showing a credit shows
 * the same one, and so that adding a required form of attribution later is one
 * edit. Renders nothing when the source names no artist, which is the case for
 * every sprite currently shipped — those are official game rips, and the
 * honest credit for them is the source directory, shown on `/credits`.
 */
export function AvatarCredit({
  avatar,
  className = 'tiny muted',
}: {
  avatar: Pick<TrainerAvatarData, 'artistCredit'> | { artistCredit: string | null } | null;
  className?: string;
}) {
  if (!avatar?.artistCredit) return null;
  return <span className={className}>Sprite by {avatar.artistCredit}</span>;
}
